"use client";

import React, { useEffect, useState } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224841" className="hidden md:flex gap-4">
        <div className="h-8 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 text-sm font-default font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 rounded-lg bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full border-b border-white/10 bg-background/95 p-6 shadow-2xl backdrop-blur-2xl md:hidden">
      <nav className="flex flex-col gap-4">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} onClick={onClose} className="block border-b border-white/5 pb-4 text-lg font-default font-semibold uppercase tracking-wider text-foreground transition-colors hover:text-primary">
              {item.label}
            </Link>
          </div>)}
        <div className="pt-4">
          <Button asChild className="w-full border border-white/20 bg-white/5 text-foreground backdrop-blur-md transition-all hover:bg-white/10 hover:shadow-[0_0_20px_rgba(0,240,255,0.3)]" size="lg">
            <Link to="#" onClick={onClose}>
              Let's Talk
            </Link>
          </Button>
        </div>
      </nav>
    </div>;
}
interface MobileMenuToggleProps {
  isOpen: boolean;
  onToggle: () => void;
}
function MobileMenuToggle({
  isOpen,
  onToggle
}: MobileMenuToggleProps) {
  return <Button variant="ghost" size="icon" className="inline-flex text-foreground md:hidden" onClick={onToggle} aria-expanded={isOpen} aria-label="Toggle navigation menu">
      {isOpen ? <X className="size-6" aria-hidden="true" /> : <Menu className="size-6" aria-hidden="true" />}
    </Button>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [isNavigating, setIsNavigating] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = window.scrollY / totalHeight * 100;
        setScrollProgress(progress);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
  }, [theme]);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (!href.startsWith("#") && !href.includes("#")) {
          setIsNavigating(true);
          setTimeout(() => setIsNavigating(false), 800);
        }
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };
  return <section id="navigation-haute-couture" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "border-b border-white/10 bg-background/80 backdrop-blur-lg" : "bg-background border-b border-transparent"}`}>
      <Progress className="fixed top-0 left-0 z-50 h-1 w-full bg-transparent" value={isNavigating ? 100 : 0} />
      <Progress className="fixed top-0 left-0 z-50 h-screen w-1 bg-transparent origin-top rotate-180" style={{
      transform: `scaleY(${scrollProgress / 100})`,
      transformOrigin: 'top'
    }} value={100} />
      <MenuProvider menu_id="16">
        <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/Design sans titre (3).png" alt="Henintsoa Andriantomady" className="h-12 w-auto object-contain transition-all duration-300 drop-shadow-[0_0_8px_rgba(0,240,255,0.4)] hover:scale-110 hover:drop-shadow-[0_0_15px_rgba(0,240,255,0.8)]" />
          </Link>

          <div className="flex flex-1 items-center justify-center">
            <DesktopNav />
          </div>

          <div className="flex shrink-0 items-center gap-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-foreground hover:bg-white/10" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </Button>

            <Button asChild className="hidden border border-white/20 bg-white/5 text-foreground backdrop-blur-md transition-all duration-300 hover:bg-white/10 hover:shadow-[0_0_25px_rgba(0,240,255,0.3)] md:inline-flex">
              <Link to="#">Let's Talk</Link>
            </Button>

            <MobileMenuToggle isOpen={isMobileMenuOpen} onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
          </div>
        </div>

        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </section>;
}